// src/services/AuthService.js
import axios from 'axios';

const BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/').replace(/\/?$/, '/');
console.log('[AuthService] URL da API:', BASE_URL);

const TOKEN_KEYS = {
  AUTH: 'cargafacil_auth_token',
  REFRESH: 'cargafacil_refresh_token',
  USER: 'cargafacil_user',
  USER_TYPE: 'cargafacil_user_type', // NOVO
};

const ENDPOINTS = {
  AUTH: {
    WEB: {
      LOGIN: 'auth/web/login/',
      REGISTER: 'auth/web/register/',
      LOGOUT: 'auth/web/logout/',
      PROFILE: 'auth/web/profile/', // não usado
    },
    MOBILE: {
      REQUEST_OTP: 'auth/mobile/request-otp/',
      VERIFY_OTP: 'auth/mobile/verify-otp/',
    },
    REFRESH: 'auth/refresh/',
    REGISTER_CLIENT: 'auth/register-client/',
    REGISTER_DRIVER: 'auth/register-driver/',
  },
};

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(token) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEYS.AUTH);
  const publicPaths = [
    ENDPOINTS.AUTH.WEB.LOGIN,
    ENDPOINTS.AUTH.WEB.REGISTER,
    ENDPOINTS.AUTH.REGISTER_CLIENT,
    ENDPOINTS.AUTH.REGISTER_DRIVER,
    ENDPOINTS.AUTH.MOBILE.REQUEST_OTP,
    ENDPOINTS.AUTH.MOBILE.VERIFY_OTP,
  ];
  if (token && !publicPaths.some((p) => config.url?.includes(p))) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem(TOKEN_KEYS.REFRESH);
      if (!refreshToken) return Promise.reject(error);

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const res = await api.post(ENDPOINTS.AUTH.REFRESH, { refresh: refreshToken });
        const newAccessToken = res.data.access;
        if (newAccessToken) {
          localStorage.setItem(TOKEN_KEYS.AUTH, newAccessToken);
          api.defaults.headers.Authorization = `Bearer ${newAccessToken}`;
          onRefreshed(newAccessToken);
          return api(originalRequest);
        }
      } catch (err) {
        console.error('[AuthService] Erro no refresh token:', err.response?.data || err.message);
        AuthService.logout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

const AuthService = {
  async register(payload, userType) {
    try {
      console.log(`[AuthService.register] Payload (${userType}):`, payload);

      let endpoint;
      if (userType === 'client') {
        endpoint = ENDPOINTS.AUTH.REGISTER_CLIENT;
      } else if (userType === 'driver') {
        endpoint = ENDPOINTS.AUTH.REGISTER_DRIVER;
      } else {
        throw new Error('Tipo de usuário inválido. Use "client" ou "driver".');
      }

      const headers = { 'Content-Type': 'application/json' };
      const res = await api.post(endpoint, payload, { headers });
      console.log('[AuthService.register] Resposta do backend:', res.data);

      const responseData = res.data.data || res.data;
      if (!responseData.token || !responseData.user) {
        throw new Error('Resposta do servidor não contém token e usuário');
      }

      localStorage.setItem(TOKEN_KEYS.AUTH, responseData.token);
      if (responseData.refresh) {
        localStorage.setItem(TOKEN_KEYS.REFRESH, responseData.refresh);
      }
      localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(responseData.user));
      localStorage.setItem(TOKEN_KEYS.USER_TYPE, userType); // 🔥 SALVA O TIPO

      return responseData;
    } catch (error) {
      console.error('[AuthService.register] Erro no registro:', error.response?.data || error.message);
      throw this.handleError(error);
    }
  },

  async login({ phone, user_type }) {
    try {
      console.log('[AuthService.login] Login attempt:', { phone, user_type });
      const res = await api.post(ENDPOINTS.AUTH.WEB.LOGIN, { phone, user_type });
      console.log('[AuthService.login] Resposta login:', res.data);

      if (res.data.token) {
        localStorage.setItem(TOKEN_KEYS.AUTH, res.data.token);
      }
      if (res.data.refresh) {
        localStorage.setItem(TOKEN_KEYS.REFRESH, res.data.refresh);
      }
      if (res.data.user) {
        localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(res.data.user));
        localStorage.setItem(TOKEN_KEYS.USER_TYPE, user_type);
      }
      return res.data;
    } catch (error) {
      console.error('[AuthService.login] Erro no login:', error.response?.data);
      throw this.handleError(error);
    }
  },

  async getProfile() {
    try {
      const userType = localStorage.getItem(TOKEN_KEYS.USER_TYPE) || 'client';
      let endpoint;
      if (userType === 'client') {
        endpoint = 'clients/profile/';
      } else if (userType === 'driver') {
        endpoint = 'drivers/profile/';
      } else {
        endpoint = 'auth/web/profile/'; // fallback
      }

      const res = await api.get(endpoint);
      if (res.data) {
        localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(res.data));
      }
      return res.data;
    } catch (error) {
      console.error('[AuthService.getProfile] Erro ao buscar perfil:', error.response?.data);
      throw this.handleError(error);
    }
  },

  async updateProfile(formData) {
    try {
      const userType = localStorage.getItem(TOKEN_KEYS.USER_TYPE) || 'client';
      let endpoint;
      if (userType === 'client') {
        endpoint = 'clients/profile/';
      } else if (userType === 'driver') {
        endpoint = 'drivers/profile/';
      } else {
        endpoint = 'auth/web/profile/';
      }

      const res = await api.put(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data) {
        localStorage.setItem(TOKEN_KEYS.USER, JSON.stringify(res.data));
      }
      return res.data;
    } catch (error) {
      console.error('[AuthService.updateProfile] Erro ao atualizar perfil:', error.response?.data);
      throw this.handleError(error);
    }
  },

  logout() {
    localStorage.removeItem(TOKEN_KEYS.AUTH);
    localStorage.removeItem(TOKEN_KEYS.REFRESH);
    localStorage.removeItem(TOKEN_KEYS.USER);
    localStorage.removeItem(TOKEN_KEYS.USER_TYPE);
    console.log('[AuthService.logout] Tokens removidos.');
    return Promise.resolve();
  },

  isAuthenticated() {
    return !!localStorage.getItem(TOKEN_KEYS.AUTH);
  },

  getUserFromStorage() {
    const userStr = localStorage.getItem(TOKEN_KEYS.USER);
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  },

  getUserTypeFromStorage() {
    return localStorage.getItem(TOKEN_KEYS.USER_TYPE) || null;
  },

  handleError(error) {
    const errData = error.response?.data || {};
    return {
      status: error.response?.status || 0,
      message: errData.message || errData.detail || 'Erro na comunicação com o servidor',
      errors: errData.errors || errData.non_field_errors || null,
      response: error.response,
    };
  },
};

export default AuthService;
export { api };